import { useEffect, useState, useRef } from 'react';
import { useTranslation } from '../i18n';

interface UserInfo {
  id: number;
  username: string;
  first_name: string;
  last_name?: string;
  photo_url?: string;
  activated: boolean;
  balance: number;
}

interface ActivationScreenProps {
  userInfo: UserInfo;
  onPremiumClick: () => Promise<void>;
  isLoading: boolean;
}

export function ActivationScreen({ userInfo, onPremiumClick, isLoading }: ActivationScreenProps) {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: t('activation.features.videoTransformation.icon'),
      title: t('activation.features.videoTransformation.title'),
      description: t('activation.features.videoTransformation.description')
    },
    {
      icon: t('activation.features.autoCaptions.icon'),
      title: t('activation.features.autoCaptions.title'),
      description: t('activation.features.autoCaptions.description')
    },
    {
      icon: t('activation.features.smartEditing.icon'),
      title: t('activation.features.smartEditing.title'),
      description: t('activation.features.smartEditing.description')
    }
  ];

  const [currentFeature, setCurrentFeature] = useState(0);
  const featureListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = featureListRef.current;
    if (!element) return;

    const handleScroll = () => {
      const scrollPosition = element.scrollLeft;
      const containerWidth = element.offsetWidth;
      const itemWidth = containerWidth * 0.8; // 80% of container width
      const gap = 16; // gap between items
      
      // Calculate the index based on scroll position, considering the gap
      const newIndex = Math.round(scrollPosition / (itemWidth + gap));
      setCurrentFeature(Math.max(0, Math.min(newIndex, features.length - 1)));
    };

    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [features.length]);

  const scrollToFeature = (index: number) => {
    const element = featureListRef.current;
    if (!element) return;

    const containerWidth = element.offsetWidth;
    const itemWidth = containerWidth * 0.8; // 80% of container width
    const gap = 16; // gap between items
    
    element.scrollTo({
      left: index * (itemWidth + gap),
      behavior: 'smooth'
    });
  };

  return (
    <div className="activation-screen">
      <div className="activation-header">
        <div className="user-avatar">
          <img 
            src={userInfo.photo_url || '/default-avatar.png'} 
            alt={userInfo.first_name}
            onError={(e) => {
              e.currentTarget.src = '/default-avatar.png';
            }}
          />
        </div>
        <h1>{t('activation.welcome', { name: userInfo.first_name })}</h1>
      </div>

      <div className="activation-content">
        <div 
          className="feature-list snap-x snap-mandatory flex overflow-x-auto px-2 -mx-2 gap-2 scrollbar-none"
          ref={featureListRef}
        >
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="feature-item snap-center flex-none w-4/5 bg-[rgba(36,129,204,0.04)] dark:bg-[#243242] rounded-xl p-4"
            >
              <span className="feature-icon text-3xl mb-3">{feature.icon}</span>
              <h3 className="text-lg font-semibold mb-2 text-[#2D62FF]">{feature.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="carousel-dots flex justify-center gap-2 mt-3">
          {features.map((_, index) => (
            <button 
              key={index} 
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentFeature 
                  ? 'bg-[#2D62FF] w-4' 
                  : 'bg-white/20 hover:bg-white/30'
              }`}
              onClick={() => scrollToFeature(index)}
            />
          ))}
        </div>

        <div className="activation-cta mt-6 px-2">
          <h2 className="text-xl font-bold mb-2">{t('activation.cta.title')}</h2>
          <p className="text-white/60 mb-6">{t('activation.cta.description')}</p>
          <button 
            onClick={onPremiumClick}
            disabled={isLoading}
            className="premium-button w-full py-3 px-4 bg-[#2D62FF] rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {isLoading ? (
              <>
                <div className="button-spinner w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                {t('activation.cta.button.processing')}
              </>
            ) : (
              <>
                <span className="star-icon">⭐️</span>
                {t('activation.cta.button.default')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
} 